/**
 * Validation contract for the sales-order create/edit form.
 *
 * Builds the Yup schema that gates when an order can leave Draft: shipping
 * and billing address fields are conditionally required based on the
 * selected shipping type (some types skip an address entirely) and the
 * "same as shipping" billing toggle, confirmation-email fields require an
 * address only when the customer is being notified, and each line item's
 * radio-billing fields are required once the item carries radios.
 *
 * Contains:
 * - getOrderSaleSchema — Yup schema factory keyed on the shipping-type list.
 */
import * as Yup from 'yup';

export const getOrderSaleSchema = shippingTypes => {
  const shipTypeSkipsAddress = shipTypeId => {
    if (shipTypeId == null) return true;
    const type = shippingTypes.find(t => t.id == shipTypeId);
    return type && !type.requires_address;
  };

  return Yup.object().shape({
    status: Yup.string().required(),
    customer: Yup.number().required('Required').typeError('Required'),
    salesperson: Yup.number().required('Required').typeError('Required'),
    // Accept either ship_company_name OR ship_contact (at least one required when shipping)
    ship_company_name: Yup.string().max(256, 'Too Long!').nullable(),
    ship_contact: Yup.string()
      .max(256, 'Too Long!')
      .nullable()
      .when('ship_type', (ship_type, schema) => {
        if (shipTypeSkipsAddress(ship_type)) {
          return schema.nullable();
        }
        // Require at least one of ship_contact or ship_company_name
        return schema.test('contact-or-company', 'Contact name or company name required', function (value) {
          const { ship_company_name } = this.parent;
          return (value && value.trim()) || (ship_company_name && ship_company_name.trim());
        });
      }),
    ship_email: Yup.string()
      .email('Invalid email')
      .max(256, 'Too Long!')
      .when(['ship_type', 'should_notify_customer'], ([ship_type, should_notify_customer], schema) => {
        if (shipTypeSkipsAddress(ship_type)) {
          return schema.nullable();
        }
        // Only required if sending confirmation email
        if (!should_notify_customer) {
          return schema.nullable();
        }
        return schema.required('Required when sending confirmation email');
      }),
    ship_address: Yup.string()
      .max(256, 'Too Long!')
      .when('ship_type', (ship_type, schema) => {
        if (shipTypeSkipsAddress(ship_type)) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    ship_city: Yup.string()
      .max(256, 'Too Long!')
      .when('ship_type', (ship_type, schema) => {
        if (shipTypeSkipsAddress(ship_type)) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    ship_state: Yup.string()
      .max(256, 'Too Long!')
      .nullable()
      .when('ship_type', (ship_type, schema) => {
        if (shipTypeSkipsAddress(ship_type)) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    ship_zip: Yup.string()
      .max(256, 'Too Long!')
      .nullable()
      .when('ship_type', (ship_type, schema) => {
        if (shipTypeSkipsAddress(ship_type)) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    ship_country: Yup.string()
      .max(256, 'Too Long!')
      .when('ship_type', (ship_type, schema) => {
        if (shipTypeSkipsAddress(ship_type)) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    ship_instructions: Yup.string().max(1024, 'Too Long!').nullable(),
    shipping_cost: Yup.number().min(0, 'Must be non-negative').nullable(),
    invoice: Yup.number().nullable(),

    ship_type: Yup.number()
      .nullable()
      .when('status', (status, schema) =>
        status != 'Draft' && status != null ? schema.required('Required').typeError('Required') : schema.nullable(),
      ),

    // Accept either bill_company_name OR bill_contact — the same rule the
    // backend enforces; skipped when billing copies shipping at submit.
    bill_company_name: Yup.string().max(256, 'Too Long!').nullable(),
    bill_contact: Yup.string()
      .max(256, 'Too Long!')
      .nullable()
      .when('billing_same_as_shipping', ([billing_same_as_shipping], schema) => {
        if (billing_same_as_shipping) {
          return schema.nullable();
        }
        return schema.test('contact-or-company', 'Contact name or company name required', function (value) {
          const { bill_company_name } = this.parent;
          return (value && value.trim()) || (bill_company_name && bill_company_name.trim());
        });
      }),
    bill_email: Yup.string()
      .email('Invalid email')
      .max(256, 'Too Long!')
      .when(
        ['should_notify_customer', 'billing_same_as_shipping'],
        ([should_notify_customer, billing_same_as_shipping], schema) => {
          // Not required if billing is same as shipping (uses ship_email)
          if (billing_same_as_shipping) {
            return schema.nullable();
          }
          // Only required if sending confirmation email
          if (!should_notify_customer) {
            return schema.nullable();
          }
          return schema.required('Required when sending confirmation email');
        },
      ),
    bill_address: Yup.string()
      .max(256, 'Too Long!')
      .when('billing_same_as_shipping', (billing_same_as_shipping, schema) => {
        if (billing_same_as_shipping) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    bill_city: Yup.string()
      .max(256, 'Too Long!')
      .when('billing_same_as_shipping', (billing_same_as_shipping, schema) => {
        if (billing_same_as_shipping) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    bill_state: Yup.string()
      .max(256, 'Too Long!')
      .when('billing_same_as_shipping', (billing_same_as_shipping, schema) => {
        if (billing_same_as_shipping) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    bill_zip: Yup.string()
      .max(256, 'Too Long!')
      .when('billing_same_as_shipping', (billing_same_as_shipping, schema) => {
        if (billing_same_as_shipping) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    bill_country: Yup.string()
      .max(256, 'Too Long!')
      .when('billing_same_as_shipping', (billing_same_as_shipping, schema) => {
        if (billing_same_as_shipping) {
          return schema.nullable();
        }
        return schema.required('Required');
      }),
    bill_auto_save: Yup.boolean().nullable(),
    date_req_by: Yup.date().required('Required').typeError('Required'),
    comments: Yup.string().max(1024, 'Too Long!').nullable(),
    hidden_comments: Yup.string().max(1024, 'Too Long!').nullable(),

    order_sale_items: Yup.array()
      .min(1, 'At least one item is required')
      .of(
        Yup.object().shape({
          item: Yup.number().required('Required').typeError('Required'),
          item_price: Yup.number().typeError('Must be a number').nullable(),
          qty_order: Yup.number()
            .required('Required')
            .integer('Quantity must be a whole number')
            .min(1, 'Missing Quantity'),
          qty_fill: Yup.number()
            .integer('Quantity must be a whole number')
            .min(0, 'Must be non-negative')
            .default(0)
            .nullable(),
          has_radios: Yup.boolean(),

          iccids: Yup.array()
            .nullable()
            .of(
              Yup.object().shape({
                iccid: Yup.string().max(20, 'Too Long!'),
              }),
            ),

          radio_billing_type: Yup.string()
            .nullable()
            .when('has_radios', (has_radios, schema) =>
              has_radios == true ? schema.required('Required') : schema.nullable(),
            ),

          prepaid_service: Yup.number()
            .nullable()
            .when(['has_radios', 'radio_billing_type'], ([has_radios, radio_billing_type], schema) =>
              has_radios == true && radio_billing_type === 'Prepaid'
                ? schema.required('Required').min(0, 'Must be non-negative')
                : schema.nullable(),
            ),

          prepaid_cost: Yup.number()
            .nullable()
            .when(['has_radios', 'radio_billing_type'], ([has_radios, radio_billing_type], schema) =>
              has_radios == true && radio_billing_type === 'Prepaid'
                ? schema.required('Required').min(0, 'Must be non-negative')
                : schema.nullable(),
            ),

          term_length_prepaid: Yup.number()
            .nullable()
            .when(['has_radios', 'radio_billing_type'], ([has_radios, radio_billing_type], schema) =>
              has_radios == true && radio_billing_type === 'Prepaid'
                ? schema.required('Required').min(0, 'Must be non-negative')
                : schema.nullable(),
            ),

          radio_billing_start_at: Yup.date()
            .nullable()
            .when(
              ['has_radios', 'radio_billing_type', 'term_length_trial'],
              ([has_radios, radio_billing_type, term_length_trial], schema) =>
                has_radios == true &&
                (radio_billing_type === 'Prepaid' ||
                  radio_billing_type === 'Date' ||
                  (term_length_trial > 0 && radio_billing_type !== 'Shipped'))
                  ? schema.required('Required')
                  : schema.nullable(),
            ),
        }),
      ),
  });
};
